import React, {useState, useEffect, useCallback} from 'react';
import {API_ENDPOINTS} from "../../../apiConfig";
import DataGrid from "../../../components/DataGrid";
import API from "../../../utils/API";
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import Forms from "../../../components/Forms";
import {ReactComponent as DeleteIcon} from '../../../assets/images/delete.svg';
import {ReactComponent as EditIcon} from '../../../assets/images/edit.svg';
import {useModal} from "../../../contexts/ModalContext";
import ConcertModal from "./ConcertModal";

const Concerts = () => {
    const [concerts, setConcerts] = useState([]);
    const {t} = useTranslation();
    const {openModal, confirm} = useModal();

    const fetchConcerts = useCallback(async () => {
        try {
            const response = await API.get(API_ENDPOINTS.concerts.list);
            setConcerts(response.data);
        } catch (error) {
            console.error("Failed to fetch concerts:", error);
        }
    }, []);

    useEffect(() => {
        fetchConcerts();
    }, [fetchConcerts]);

    const confirmDeleteConcert = async (concert) => {
        const msg = t('concerts.confirmDeletionMessage', {title: concert.location});
        const result = await confirm(msg, {});
        if (result === true) {
            try {
                await API.delete(API_ENDPOINTS.concerts.delete(concert._id));
                setConcerts(prev => prev.filter(item => item._id !== concert._id));
            } catch (error) {
                console.error("Failed to delete concert:", error);
            }
        }
    };

    const openConcertModal = (concert) => {
        openModal(
            ConcertModal,
            {
                concert,
                onConcertUpdate: fetchConcerts
            },
            {title: concert ? t('concerts.editConcert') : t('concerts.addConcert')}
        );
    };

    const columns = [
        {
            key: 'location',
            name: t('concerts.location'),
            renderCell: ({row}) => (
                <label className="flex items-center gap-2 w-full">
                    <span className="min-w-0 flex-1 truncate" title={row.title}>
                    {row.location}
                    </span>
                    <div className="flex items-center gap-2 flex-none shrink-0">
                        <DeleteIcon className="w-4 cursor-pointer fill-red-700 m-2"
                                    onClick={() => confirmDeleteConcert(row)}/>
                        <EditIcon className="w-4 cursor-pointer text-blue-800 h-full"
                                  onClick={() => openConcertModal(row)}/>
                    </div>
                </label>
            )
        },
        {
            key: 'slug',
            name: t('concerts.slug')
        },
        {
            key: 'date',
            name: t('concerts.date'),
            renderCell: ({row}) => row.date ? moment(row.date).format('DD.MM.YYYY HH:mm') : '-'
        },
        {
            key: 'description',
            name: t('concerts.description'),
            renderCell: ({row}) => (row.description || '').split('\n')[0]?.slice(0, 140) + ((row.description || '').length > 140 ? '…' : '')
        },
        {
            key: 'ticketUrl',
            name: t('concerts.ticketUrl'),
            renderCell: ({row}) => row.ticketUrl
                ? <a className="text-blue-700 underline" href={row.ticketUrl} target="_blank"
                     rel="noreferrer">{t('concerts.ticket')}</a>
                : '-'
        },
        {
            key: 'createdAt',
            name: t('createdAt'),
            renderCell: ({row}) => row.createdAt ? moment(row.createdAt).format('DD.MM.YYYY HH:mm') : '-'
        }
    ].map(c => ({...c, resizable: true}));

    return (
        <div>
            <Forms.Button label={t('concerts.addConcert')} onClick={() => openConcertModal()} className="mb-8"/>
            <DataGrid rows={concerts} columns={columns}/>
        </div>
    );
};

export default Concerts;
